import {
  Activity,
  Archive,
  ClipboardList,
  ClipboardPaste,
  Moon,
  Target,
  Trophy,
  UsersRound,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';

const tabs = [
  { to: '/stat-intel', label: 'Stat Intel', mobileLabel: 'Stats', icon: Activity },
  { to: '/people', label: 'People', mobileLabel: 'People', icon: UsersRound },
  { to: '/clients', label: 'Clients', mobileLabel: 'Clients', icon: Trophy },
  { to: '/prospects', label: 'Prospects', mobileLabel: 'Prospects', icon: Target },
  { to: '/ir', label: 'IR', mobileLabel: 'IR', icon: ClipboardList },
  { to: '/batch-notes', label: 'Batch Notes', mobileLabel: 'Batch', icon: ClipboardPaste },
  { to: '/nightly-sweep', label: 'Nightly Sweep', mobileLabel: 'Night', icon: Moon },
  { to: '/archive', label: 'Archive', mobileLabel: 'Arch', icon: Archive },
  // Endorsements and Schedule are off the bar for now (Scott, 2026-10-20); their
  // pages still open from a player's profile.
] as const;

export function Layout() {
  return (
    <div className="min-h-dvh overflow-x-clip bg-bg text-ink">
      <header className="relative z-40 border-b border-white/10 bg-bartlett/95 text-white shadow-[0_10px_30px_rgba(0,0,0,0.14)] backdrop-blur">
        <div className="mx-auto flex min-h-14 w-full max-w-shell flex-wrap items-center justify-between gap-2 overflow-hidden px-3 py-2 sm:min-h-16 sm:gap-3 sm:overflow-visible sm:px-shell sm:py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-white shadow-sm ring-1 ring-white/20 sm:h-10 sm:w-10">
              <img
                src="https://bartletthockey.com/wp-content/uploads/2019/08/BH-Logo-MAIN-300x108.png"
                alt="Bartlett Hockey"
                className="max-h-8 max-w-8 object-contain"
              />
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <div className="truncate font-mono text-[17px] font-semibold leading-none tracking-normal sm:text-[18px]">
                  Faceoff
                </div>
                <span className="shrink-0 font-mono text-[10px] font-semibold text-bartlettGold">
                  v2.0
                </span>
              </div>
              <div className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-bartlettGold sm:mt-1 sm:text-[11px] sm:tracking-[0.16em]">
                Elite Player Management Since 1984
              </div>
            </div>
          </div>

          <nav
            aria-label="Primary desktop"
            className="hidden min-w-0 flex-wrap items-center justify-end gap-1 sm:flex"
          >
            {tabs.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  [
                    'focus-ring inline-flex h-10 shrink-0 items-center gap-2 rounded-control border px-3 text-sm font-semibold leading-none transition-all',
                    isActive
                      ? 'border-bartlettGold bg-bartlettGold text-bartlett shadow-sm'
                      : 'border-transparent text-white/70 hover:border-white/20 hover:bg-white/10 hover:text-white',
                  ].join(' ')
                }
              >
                <Icon aria-hidden="true" className="shrink-0" size={17} strokeWidth={2.1} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <nav
          aria-label="Primary"
          className="mx-auto grid w-full max-w-[30rem] grid-cols-4 gap-1 px-3 pb-2 sm:hidden"
        >
          {tabs.map(({ to, mobileLabel, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                [
                  'focus-ring inline-flex h-10 min-w-0 flex-col items-center justify-center gap-0.5 rounded-control border px-1 text-[10px] font-semibold transition-all',
                  isActive
                    ? 'border-bartlettGold bg-bartlettGold text-bartlett shadow-sm'
                    : 'border-transparent text-white/70 hover:border-white/20 hover:bg-white/10 hover:text-white',
                ].join(' ')
              }
            >
              <Icon aria-hidden="true" size={15} strokeWidth={2.1} />
              <span className="truncate">{mobileLabel}</span>
            </NavLink>
          ))}
        </nav>
      </header>

      {/* clip, not hidden, here and on the outer div: hidden makes a scroll box that breaks sticky bars. */}
      <main className="mx-auto w-full max-w-shell min-w-0 overflow-x-clip px-3 py-4 sm:px-shell sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
